import { useEffect, useRef, useState } from "react";
import React from "react";

import classNames from "classnames/bind";
import Button from "common/blocks/Button";
import DynamicPhoneNumber from "common/blocks/DynamicPhoneNumber";
import EstimatedMarketPriceSidePanel from "common/blocks/Sidebar/SavingsPriceSidePanel";
import UIIcon from "common/blocks/UIIcon";
import useModalContext from "common/providers/modal/useModalContext";
import useWhoAmIContext from "common/providers/whoAmI/useWhoAmIContext";
import { ResolvedMegaMenuCtaButton } from "resolvers/data/header/megaMenuResolver";
import enquiryLinkResolver from "resolvers/data/pdp/enquiryLinkResolver";
import SavingsSidePanelContentResolver, {
    SavingsSidePanelContentProps,
} from "resolvers/data/pdp/savingsSidePanelContentResolver";
import { ResolvedVehicleDisplaySettings } from "resolvers/pages/pdpPageResolver";
import { DynamicSidebar } from "services/cms/models/dynamicSidebar";
import { VehicleService } from "services/vehicle/vehicle";
import slugify from "slugify";
import formatCurrency from "utils/formatCurrency";
import regYearLetter from "utils/regYearLetter";
import { v4 } from "uuid";

import styles from "./SavingsBanner.module.scss";

const cx = classNames.bind(styles);

export type ListSavingsBannerProps = {
    components: {
        title: string;
        sidePanelAnchorText: string;
        listPriceText: string;
        ourPriceText: string;
        savingsText: string;
        cta?: ResolvedMegaMenuCtaButton;
        vehicleDisplaySettings: ResolvedVehicleDisplaySettings;
    };
    vehicle: VehicleService.Vehicle;
    formattedModel: string;
    formattedMake: string;
    formattedVariant: string;
    dynamicSavingsSidebar: DynamicSidebar;
};

const listPriceId = "pdp-list-price";
const ourPriceId = "pdp-our-price";
const savingsAmountId = "pdp-savings-value";
const hideableBubbleElementIdsList = [listPriceId, ourPriceId, savingsAmountId];

const ListSavingsBanner = ({
    components,
    vehicle,
    formattedVariant,
    formattedMake,
    formattedModel,
    dynamicSavingsSidebar,
}: ListSavingsBannerProps) => {
    const blueBorderClassName = "blue-border";
    const roundedCornersClassName = "bubble-last-of-type";
    const hiddenInlineStyle = { display: "none" };
    const [lastVisibleBubbleId, setLastVisibleBubbleId] = useState(savingsAmountId); //default view is set so these are viewable: vehicle new list price, savings amount and cta
    const [hiddenBubbleIds, setHiddenBubbleIdsClassName] = useState([ourPriceId]);

    const basicPrice = vehicle?.CurrentPrice?.SalePrice?.BasicPrice ?? 0;
    const vatAmount = vehicle?.CurrentPrice?.SalePrice?.VATAmount ?? 0;

    const vehicleTotalPrice = vehicle.Taxonomy.IsCommercial ? basicPrice : basicPrice + vatAmount;

    const originalRetailPriceBasic = vehicle?.OriginalRetailPrice?.BasicPrice ?? 0;
    const originalRetailPriceVat = vehicle?.OriginalRetailPrice?.VATAmount ?? 0;

    const listPriceValue = vehicle.Taxonomy.IsCommercial
        ? originalRetailPriceBasic
        : originalRetailPriceBasic + originalRetailPriceVat;
    const savingsValue = listPriceValue - vehicleTotalPrice;

    const { isLoggedIn } = useWhoAmIContext();
    const id = vehicle.LegacyVehicleId ?? vehicle.StockBookNumber;
    const variant = slugify(formattedVariant, { lower: true });
    const make = slugify(formattedMake, { lower: true });
    const model = slugify(formattedModel, { lower: true });
    const page = isLoggedIn ? "personal-details" : "create-account";

    const reserveBuyUrl = `/used-cars/${make}/${model}/${variant}/${id}/reserve/${v4()}/${page}`;
    const acceptedRegList =
        components.vehicleDisplaySettings.listPriceSavingRegistrationYearCriteria.split(",");
    const regPlateAgeIdentifier = regYearLetter(new Date(vehicle.DateOfFirstUKRegistration)) ?? "";
    const isViewableReg = acceptedRegList.includes(regPlateAgeIdentifier);

    const mutationRef = useRef<HTMLDivElement>(null);
    function styleChangedCallback() {
        handleSetLastVisibleBubbleStyling();
    }
    const handleSetLastVisibleBubbleStyling = () => {
        let lastVisibleElementID = "";
        let hiddenElementsList: string[] = [];
        hideableBubbleElementIdsList.forEach(function (value) {
            //evaluate each element possible to be made hidden; check if it is currently hidden;
            let elementMarkup = document.getElementById(value);
            if (elementMarkup?.style.display !== "none") {
                lastVisibleElementID = value;
            } else if (elementMarkup?.style.display === "none") {
                hiddenElementsList.push(value);
            }
        });
        setLastVisibleBubbleId(lastVisibleElementID);
        setHiddenBubbleIdsClassName(hiddenElementsList);
    };

    useEffect(() => {
        handleSetLastVisibleBubbleStyling();
    }, []);

    const observer = typeof window !== "undefined" && new MutationObserver(styleChangedCallback); //set up observer to set styling for last bubble element on style change
    mutationRef.current && observer
        ? observer.observe(mutationRef.current, {
              childList: true,
              subtree: true,
              attributes: true,
              attributeFilter: ["style"],
          })
        : null;

    const { useSidebar } = useModalContext();
    const { openSidebar } = useSidebar();
    const { savingsSidePanel, savingsSidePanelTitle } = getSavingsSidePanelProps(
        vehicle,
        dynamicSavingsSidebar
    );

    const openSavingsSidebar = () => {
        openSidebar({
            title: savingsSidePanelTitle,
            content: {
                component: () => (
                    <EstimatedMarketPriceSidePanel
                        {...savingsSidePanel}
                        reserveLink={reserveBuyUrl}
                        isComingSoon={vehicle.ComingSoonDetails?.IsComingSoon ?? false}
                    />
                ),
            },
        });
    };

    return (
        <div className={cx("savings-banner-container")}>
            {components.vehicleDisplaySettings.listPriceSavingThreshold < savingsValue &&
                isViewableReg && (
                    <>
                        <div className={cx("savings-banner")}>
                            <div className={cx("titleContainer")}>
                                <div
                                    className={cx("title")}
                                    dangerouslySetInnerHTML={{
                                        __html: components.title.replace(
                                            "{listPriceSaving}",
                                            formatCurrency(savingsValue, 0)
                                        ),
                                    }}
                                ></div>
                                <Button
                                    theme="textLink"
                                    onClick={openSavingsSidebar}
                                    ariaLabel={components.sidePanelAnchorText}
                                    type="button"
                                >
                                    {components.sidePanelAnchorText}
                                </Button>
                            </div>
                            <div className={cx("flowContainer")}>
                                <div
                                    className={cx("detailsContainer")}
                                    id="pdp-details-container"
                                    ref={mutationRef}
                                >
                                    <div
                                        className={cx(
                                            "bubble",
                                            `${
                                                lastVisibleBubbleId === listPriceId
                                                    ? roundedCornersClassName
                                                    : ""
                                            }`,
                                            `${
                                                lastVisibleBubbleId === listPriceId
                                                    ? blueBorderClassName
                                                    : ""
                                            }`
                                        )}
                                        id={listPriceId}
                                        style={
                                            hiddenBubbleIds.includes(listPriceId)
                                                ? hiddenInlineStyle
                                                : {}
                                        }
                                    >
                                        <div className={cx("icon")}>
                                            <UIIcon
                                                svgProps={{ className: cx("icon") }}
                                                icon="PriceTag"
                                            ></UIIcon>
                                        </div>
                                        <div className={cx("details")}>
                                            <div className={cx("header")}>
                                                {components.listPriceText}
                                            </div>
                                            <div
                                                className={cx("value")}
                                                dangerouslySetInnerHTML={{
                                                    __html: formatCurrency(listPriceValue, 0),
                                                }}
                                            ></div>
                                        </div>
                                    </div>
                                    <div
                                        className={cx(
                                            "bubble",
                                            `${
                                                lastVisibleBubbleId === ourPriceId
                                                    ? roundedCornersClassName
                                                    : ""
                                            }`,
                                            `${
                                                lastVisibleBubbleId === ourPriceId
                                                    ? blueBorderClassName
                                                    : ""
                                            }`
                                        )}
                                        id={ourPriceId}
                                        style={
                                            hiddenBubbleIds.includes(ourPriceId)
                                                ? hiddenInlineStyle
                                                : {}
                                        }
                                    >
                                        <div className={cx("icon")}>
                                            <UIIcon
                                                svgProps={{ className: cx("icon") }}
                                                icon="MotorpointCircleLogo"
                                            ></UIIcon>
                                        </div>
                                        <div className={cx("details")}>
                                            <div className={cx("header")}>
                                                {components.ourPriceText}
                                            </div>
                                            <div
                                                className={cx("value")}
                                                dangerouslySetInnerHTML={{
                                                    __html: formatCurrency(vehicleTotalPrice, 0),
                                                }}
                                            ></div>
                                        </div>
                                    </div>
                                    <div
                                        className={cx(
                                            "bubble",
                                            `${
                                                lastVisibleBubbleId === savingsAmountId
                                                    ? roundedCornersClassName
                                                    : ""
                                            }`,
                                            `${
                                                lastVisibleBubbleId === savingsAmountId
                                                    ? blueBorderClassName
                                                    : ""
                                            }`
                                        )}
                                        id={savingsAmountId}
                                        style={
                                            hiddenBubbleIds.includes(savingsAmountId)
                                                ? hiddenInlineStyle
                                                : {}
                                        }
                                    >
                                        <div className={cx("icon")}>
                                            <UIIcon
                                                svgProps={{ className: cx("icon") }}
                                                icon="SaveCalculator"
                                            ></UIIcon>
                                        </div>

                                        <div className={cx("details")}>
                                            <div className={cx("header")}>
                                                {components.savingsText}
                                            </div>
                                            <div
                                                className={cx("value")}
                                                dangerouslySetInnerHTML={{
                                                    __html: formatCurrency(savingsValue, 0),
                                                }}
                                            ></div>
                                        </div>
                                    </div>
                                </div>
                                {!vehicle.ComingSoonDetails?.IsComingSoon && (
                                    <div
                                        className={cx("savings-banner-cta-container")}
                                        id="reserve-or-buy-cta"
                                    >
                                        <Button
                                            className={cx("cta", "primary")}
                                            href={reserveBuyUrl}
                                            ariaLabel="Reserve or Buy"
                                            rel="nofollow"
                                        >
                                            Reserve or Buy
                                        </Button>
                                    </div>
                                )}
                                {vehicle.ComingSoonDetails?.IsComingSoon && (
                                    <div
                                        className={cx("savings-banner-cta-container")}
                                        id="call-us-now-cta"
                                    >
                                        <Button
                                            className={cx("cta", "primary", "reserve")}
                                            ariaLabel="Call us to reserve"
                                        >
                                            <DynamicPhoneNumber
                                                isLink
                                                prefix="Call us to reserve"
                                                iconName="PhoneEmpty"
                                                showNumber={false}
                                            />
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )}
        </div>
    );
};

export default ListSavingsBanner;

function getSavingsSidePanelProps(
    vehicle: VehicleService.Vehicle,
    dynamicSavingsSidebar: DynamicSidebar
) {
    const enquiryLink = enquiryLinkResolver(vehicle);
    const basicPrice = vehicle?.CurrentPrice?.SalePrice?.BasicPrice ?? 0;
    const VATAmount = vehicle?.CurrentPrice?.SalePrice?.VATAmount ?? 0;

    const price = basicPrice + VATAmount;
    const originalRetailPriceBasic = vehicle?.OriginalRetailPrice?.BasicPrice ?? 0;
    const originalRetailPriceVat = vehicle?.OriginalRetailPrice?.VATAmount ?? 0;
    const originalRetailPrice = vehicle.Taxonomy.IsCommercial
        ? originalRetailPriceBasic
        : originalRetailPriceBasic + originalRetailPriceVat;
    const savingsAgainstOriginalRetailPrice = vehicle.Taxonomy.IsCommercial
        ? originalRetailPrice - basicPrice
        : originalRetailPrice - price;

    const estimatedMarketPrice = vehicle?.EstimatedMarketPrice ?? 0;
    const savingsAgainstEstimatedMarketPrice = vehicle.Taxonomy.IsCommercial
        ? estimatedMarketPrice - basicPrice
        : estimatedMarketPrice - price;

    const ourPrice = vehicle.Taxonomy.IsCommercial ? basicPrice : price;

    const savingsSidePanelTitle =
        dynamicSavingsSidebar.elements.firstLinkedSidebar.linkedItems[0].elements.title.value;

    const savingsSidePanel = SavingsSidePanelContentResolver({
        ourPrice,
        enquiryLink,
        estimatedMarketPrice,
        originalRetailPrice,
        savingsAgainstEstimatedMarketPrice,
        savingsAgainstOriginalRetailPrice,
        showEstimatedMarketPriceSaving: false,
        dynamicSavingsSidebar,
    } as SavingsSidePanelContentProps);

    return { savingsSidePanel, savingsSidePanelTitle };
}
